// @givmo/app-charity — the split auth layout: a left brand panel (gradient + lockup + the
// ORIGINAL presence-management value copy — an OPERATOR RULING, see the panel comment below) and a
// right pane carrying the auth card. The panel is decorative/orienting and hidden below `lg`; the
// funnel's work-email nudge lives on the unified door's subtitle (auth-door.tsx), which renders at
// /sign-up — where a signed-out caller of the outreach email's deep link (`/claim/<id>`) is
// forwarded (D-CP-64). Skip-to-content targets the main pane (floor B11).

import type { ReactNode } from 'react';
import { GivmoWordmark, GivmoLogomark, testIdProp } from '@givmo/design-system';
import { MAIN_CONTENT_ID, SkipToContent } from '../../components/skip-to-content';

export interface AuthShellProps {
  children: ReactNode;
  testId: string;
  /** Optional wider column for the multi-field sign-up screen. */
  wide?: boolean;
}

export function AuthShell({ children, testId, wide = false }: AuthShellProps) {
  return (
    <div className="flex min-h-screen bg-surface-sunken" {...testIdProp(testId)}>
      <SkipToContent />
      {/* ── Left brand panel (lg+) ───────────────────────────────────────────────────────────── */}
      <aside className="relative hidden w-[44%] max-w-[560px] shrink-0 overflow-hidden lg:flex lg:flex-col">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 55% at 80% 0%, rgba(104,141,240,0.45) 0%, rgba(104,141,240,0) 55%),' +
              'radial-gradient(60% 50% at 0% 100%, rgba(24,36,70,0.65) 0%, rgba(24,36,70,0) 60%),' +
              'linear-gradient(145deg, var(--color-brand-600) 0%, var(--color-brand-700) 48%, var(--color-brand-800) 100%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 top-32 size-[280px] rounded-full opacity-40 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(151,180,246,0.7) 0%, rgba(151,180,246,0) 70%)',
          }}
        />
        <GivmoLogomark
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -right-12 h-[360px] w-auto select-none text-white/[0.06]"
        />
        {/* Footer scrim. The © line's contrast was a property of WHERE the decorative radials happened
            to land (3.13:1 against the panel's lightest blend); this makes it a property of the
            composition instead — the bottom band is deterministically dark, so white/85 clears 4.5:1
            against every blend the gradient can produce, not just the one under the text today. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[180px]"
          style={{
            background: 'linear-gradient(to top, rgba(18,25,38,0.45) 0%, rgba(18,25,38,0) 100%)',
          }}
        />

        <div className="relative flex h-full flex-col p-44">
          <div className="flex items-center">
            <GivmoWordmark className="h-[28px] w-auto text-white" />
          </div>

          {/* OPERATOR RULING 2026-08-11 (Jonathan, design pass CPA-DP1): this panel keeps its
              ORIGINAL presence-management content, verbatim. A money-first rewrite ("Direct deposit
              and exact statements…" + a numbered step list) shipped briefly and was rejected the
              same night — do not re-propose it here. The funnel's orientation/value copy lives on
              the sign-up CARD and the claim screens instead. Sole delta from the original: the
              © line is white/70, not white/55 (contrast floor B7 — 4.5:1 over the gradient). */}
          <div className="mt-auto max-w-[400px] pb-16">
            <p className="font-sans text-s32 font-w700 leading-h12 tracking-givmo text-white">
              Manage your nonprofit&apos;s presence on Givmo.
            </p>
            <p className="mt-16 font-sans text-s15 font-w400 leading-h15 tracking-givmo text-white/75">
              Claim your organization, set up how you receive funds, and see the donations
              supporters have contributed — all in one place.
            </p>
            <ul className="mt-24 flex flex-col gap-10">
              {[
                'Claim and verify your organization',
                'Set up payments to receive donations',
                'Track grants and donation activity',
              ].map((point) => (
                <li
                  key={point}
                  className="flex items-center gap-10 font-sans text-s14 font-w400 tracking-givmo text-white/80"
                >
                  <span className="flex size-18 shrink-0 items-center justify-center rounded-full bg-white/15">
                    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden>
                      <path
                        d="M2.5 6.2l2.2 2.2 4.8-4.8"
                        fill="none"
                        stroke="white"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <p className="font-sans text-s13 font-w400 tracking-givmo text-white/70">
            © {new Date().getFullYear()} Givmo. Charitable giving, made simple.
          </p>
        </div>
      </aside>

      {/* ── Right auth pane ──────────────────────────────────────────────────────────────────── */}
      {/* B11 applies HERE most of all: this shell is the first screen a cold outreach recipient sees
          (forwarded from the email's `/claim/<id>` deep link to /sign-up), so it is the first thing a
          keyboard or screen-reader caller meets — and the brand panel is a long run of decorative
          and orientation content sitting in front of the form in the DOM. */}
      <main
        id={MAIN_CONTENT_ID}
        tabIndex={-1}
        className="flex flex-1 flex-col items-center justify-center px-20 py-32 outline-none"
      >
        <div className={`flex w-full flex-col ${wide ? 'max-w-[34rem]' : 'max-w-[26rem]'}`}>
          <div className="mb-24 flex items-center justify-center lg:hidden">
            <GivmoWordmark className="h-[24px] w-auto text-brand-500" />
          </div>

          <div className="flex flex-col gap-20 rounded-2xl border border-border-subtle bg-surface p-32 shadow-sm">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
